import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type ModalSheetSize = "md" | "lg" | "xl";

const maxWidthClass: Record<ModalSheetSize, string> = {
  md: "md:max-w-xl",
  lg: "md:max-w-2xl",
  xl: "md:max-w-3xl",
};

/** Duration for the close (exit) animation — fast and snappy. */
const CLOSE_DURATION_MS = 250;
const SWIPE_CLOSE_THRESHOLD_PX = 120;

type Props = {
  isOpen: boolean;
  size?: ModalSheetSize;
  /**
   * Called when the user dismisses the sheet (backdrop click, swipe down, Escape).
   * The parent is responsible for setting isOpen=false in response.
   */
  onClose?: () => void;
  children: React.ReactNode;
};

/**
 * Responsive modal wrapper.
 *
 * Mobile  → slides up from bottom as a sheet; draggable down to dismiss.
 * Desktop → centered modal with subtle scale + fade entrance.
 *
 * iOS safe area is handled automatically via a spacer at the bottom.
 * No child needs to add env(safe-area-inset-bottom) manually.
 */
export function ModalSheet({ isOpen, size = "md", onClose, children }: Props) {
  // isMounted stays true during the close animation so the element is in DOM.
  const [isMounted, setIsMounted] = useState(isOpen);
  // isVisible drives the CSS animation class (translate-y-0 vs translate-y-full).
  const [isVisible, setIsVisible] = useState(false);

  // Freeze the rendered children through the close animation so a parent that
  // clears its state on close doesn't flash empty/default content for the exit.
  const frozenChildrenRef = useRef<React.ReactNode>(children);
  useEffect(() => {
    if (isOpen) frozenChildrenRef.current = children;
  }, [isOpen, children]);

  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  // Stable ref to onClose so event listeners never go stale.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Guard: if a swipe-dismiss animation is already running, ignore the isOpen=false
  // that the parent fires in response to our onClose call.
  const isSwipeClosingRef = useRef(false);

  // Animate close triggered by the parent setting isOpen=false.
  const animateClose = useCallback(() => {
    if (isSwipeClosingRef.current) return; // swipe already handling it
    setIsVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsMounted(false);
    }, CLOSE_DURATION_MS);
  }, []);

  // Mount during render when the sheet opens (previous-value comparison —
  // no setState-in-effect); the visibility flip stays in the effect below
  // because it must wait a real paint frame for the CSS transition.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setIsMounted(true);
  }

  // React to isOpen prop changes.
  useEffect(() => {
    if (isOpen) {
      isSwipeClosingRef.current = false;
      // setTimeout(16) guarantees a real paint frame between mount and the
      // class change, so the CSS transition always plays. Double-RAF can still
      // batch in React's concurrent mode and skip the animation entirely.
      closeTimerRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 16);
    } else {
      animateClose();
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, animateClose]);

  // Escape key.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  // ─── Drag to dismiss ──────────────────────────────────────────────────────
  // All drag state lives in refs — zero React re-renders during touchmove.
  const touchStartY = useRef<number>(0);
  const currentDragY = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.changedTouches[0]?.clientY ?? 0;
    currentDragY.current = 0;
    // Disable CSS transition while the user is dragging (instant follow).
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Only allow dragging downward.
    const delta = Math.max(
      0,
      (e.changedTouches[0]?.clientY ?? 0) - touchStartY.current,
    );
    currentDragY.current = delta;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${delta}px)`;
    }
    // Proportionally dim the backdrop as the sheet is dragged down.
    if (backdropRef.current) {
      const opacity = Math.max(0, 0.4 - (delta / 300) * 0.4);
      backdropRef.current.style.opacity = String(opacity);
    }
  };

  const handleTouchEnd = () => {
    const delta = currentDragY.current;

    // Re-enable the CSS transition.
    if (sheetRef.current) sheetRef.current.style.transition = "";

    if (delta > SWIPE_CLOSE_THRESHOLD_PX) {
      // Past threshold — continue sliding off screen with CSS transition.
      if (sheetRef.current) {
        sheetRef.current.style.transform = "translateY(100%)";
      }
      // Fade out the backdrop in sync.
      if (backdropRef.current) {
        backdropRef.current.style.transition = `opacity ${CLOSE_DURATION_MS}ms ease-in`;
        backdropRef.current.style.opacity = "0";
      }
      setTimeout(() => {
        // Reset all inline overrides.
        if (sheetRef.current) sheetRef.current.style.transform = "";
        if (backdropRef.current) {
          backdropRef.current.style.transition = "";
          backdropRef.current.style.opacity = "";
        }
        // Unmount and notify parent — set the guard so the parent's isOpen=false
        // response doesn't trigger a duplicate close animation.
        isSwipeClosingRef.current = true;
        setIsMounted(false);
        onCloseRef.current?.();
      }, CLOSE_DURATION_MS);
    } else {
      // Below threshold — snap back to open position.
      if (sheetRef.current) sheetRef.current.style.transform = "";
      if (backdropRef.current) backdropRef.current.style.opacity = "";
      currentDragY.current = 0;
    }
  };

  const handleTouchCancel = () => {
    // System-interrupted gesture — snap back cleanly.
    if (sheetRef.current) {
      sheetRef.current.style.transition = "";
      sheetRef.current.style.transform = "";
    }
    if (backdropRef.current) backdropRef.current.style.opacity = "";
    currentDragY.current = 0;
  };
  // ─────────────────────────────────────────────────────────────────────────

  if (!isMounted) return null;
  if (typeof document === "undefined") return null;

  // Portal to <body> so `position: fixed` anchors to the viewport even when an
  // ancestor is transformed (e.g. a page-transition container).
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={[
          "fixed inset-0 z-40 bg-black/40 transition-opacity",
          // Backdrop mirrors the sheet: slow fade-in, fast fade-out
          isVisible
            ? "opacity-100 duration-[450ms]"
            : "opacity-0 duration-[250ms]",
        ].join(" ")}
        aria-hidden="true"
        onClick={() => onCloseRef.current?.()}
      />

      {/* Sheet positioner */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-end md:items-center md:justify-center md:p-6">
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
          className={[
            "pointer-events-auto flex w-full flex-col",
            "border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-lg)]",
            // Mobile: bottom sheet sizing and shape
            "max-h-[92vh] rounded-t-2xl",
            // Desktop: centered modal sizing and shape
            `md:rounded-2xl md:max-h-[90vh] ${maxWidthClass[size]}`,
            // Transition applies to transform + opacity + scale.
            // The duration/easing in each state applies while LEAVING that state,
            // so open-curve lives in the closed class and close-curve in the open class.
            "transition-all will-change-transform",
            isVisible
              ? // Open: at rest — close animation runs from here (fast, ease-in)
                "translate-y-0 opacity-100 md:scale-100 duration-[250ms] ease-in"
              : // Closed: enter animation runs from here (slow start → expo-out, very visible)
                "translate-y-full md:translate-y-0 md:scale-[0.97] md:opacity-0 duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
          ].join(" ")}
        >
          {/* Drag handle pill — visual affordance, mobile only */}
          <div
            className="flex shrink-0 justify-center pb-1 pt-2.5 md:hidden"
            aria-hidden="true"
          >
            <div className="h-1 w-10 rounded-full bg-[color:var(--border-light)]" />
          </div>

          {/*
           * Intentional render-phase ref read (the exit-animation escape hatch).
           * The ref is WRITTEN only while `isOpen` (see the effect above) and READ
           * only while `!isOpen`, so no render ever reads a value it just mutated:
           * the value read here was committed by a previous render's effect. That
           * is what the rule guards against, so this is a false positive.
           *
           * The alternatives are worse: holding it in state means a setState either
           * in an effect or during render (both flagged, both add a render pass),
           * and dropping the freeze makes the sheet flash empty on close whenever a
           * parent clears its state — the exact bug this exists to prevent.
           */}
          {/* eslint-disable-next-line react-hooks/refs */}
          {isOpen ? children : frozenChildrenRef.current}

          {/*
           * Safe area spacer — equals the iOS home-indicator inset.
           * Always 0px on desktop so this is invisible there.
           */}
          <div
            aria-hidden="true"
            className="h-[env(safe-area-inset-bottom)] shrink-0"
          />
        </div>
      </div>
    </>,
    document.body,
  );
}
// This was a stale fork of the @a2z/ui ModalSheet whose mount state machine
// drifted and broke (open sheets rendered null — share menus, product
// actions, variant editor all silently dead). The shared implementation is
// the single source of truth; this re-export keeps existing import paths.
